import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { EntityLink } from "@/components/EntityLink";
import { api, euro } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { PurchaseReceipt } from "@/components/PurchaseReceipt";

const RECEIPT_OK = ["completed", "paid", "shipped", "delivered", "pending_activation"];

const CAT_LABEL = { sim: "SIM", phone: "Smartphone", ricarica: "Ricarica", accessorio: "Accessorio", spedizione: "Spedizione", gift: "Gift card", giftcard: "Gift card", esim: "eSIM", servizio: "Servizio" };
const catLabel = (c) => CAT_LABEL[c] || (c ? c.charAt(0).toUpperCase() + c.slice(1) : "—");
const refNo = (o) => (o.ref_no ? `#${o.ref_no}` : "—");
const STATUS = { completed: ["Completato", "bg-emerald-500"], paid: ["Pagato", "bg-emerald-500"], shipped: ["Spedito", "bg-emerald-500"],
  pending: ["In attesa", "bg-amber-500"], awaiting_payment: ["In attesa di pagamento", "bg-amber-500"], awaiting_transfer: ["In attesa di bonifico", "bg-amber-500"], needs_review: ["Da verificare", "bg-amber-500"], refund_processing: ["Rimborso in corso", "bg-amber-500"],
  draft: ["Bozza", "bg-slate-400"], refunded: ["Rimborsato", "bg-slate-400"],
  failed: ["Non riuscito", "bg-red-500"], not_completed: ["Non completato", "bg-red-500"], error: ["Errore", "bg-red-500"], cancelled: ["Annullato", "bg-red-500"] };

const Tip = ({ children, content, testid }) => (
  <Tooltip><TooltipTrigger asChild><span className="cursor-help" data-testid={testid}>{children}</span></TooltipTrigger><TooltipContent>{content}</TooltipContent></Tooltip>
);

const StatusDot = ({ o }) => {
  const [label, color] = STATUS[o.status] || [o.status ? o.status.charAt(0).toUpperCase() + o.status.slice(1).replace(/_/g, " ") : "Sconosciuto", "bg-slate-400"];
  return <span className="inline-flex items-center gap-2 text-sm whitespace-nowrap" data-testid={`order-status-${o.id}`}><span className={`inline-block w-2 h-2 rounded-full shrink-0 ${color}`} aria-hidden="true" />{label}</span>;
};

const OrdersToolbar = ({ q, setQ, cat, setCat, cats, count }) => (
  <div className="flex flex-wrap items-center gap-2" data-testid="orders-toolbar">
    <div className="relative w-full sm:w-72"><Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca per N°, prodotto o numero" className="pl-9" data-testid="orders-search" /></div>
    <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 bg-background border border-border rounded-md text-sm px-3" data-testid="orders-category-filter"><option value="">Tutte le categorie</option>{cats.map((c) => <option key={c} value={c}>{catLabel(c)}</option>)}</select>
    <span className="text-xs text-muted-foreground sm:ml-auto" data-testid="orders-count">{count} {count === 1 ? "ordine" : "ordini"}</span>
  </div>
);

const CostInfo = ({ e }) => !e || e.cost === null ? <span>Costo non disponibile</span> : <div className="space-y-0.5 text-xs">
  <p>Costo: <strong>{euro(e.cost)}</strong>{e.estimated ? " (stimato)" : ""}</p>
  <p>IVA: <strong>{e.vat_rate === null ? "da definire" : `${e.vat_rate}%`}</strong></p>
  <p>Costo IVA inclusa: <strong>{euro(e.cost_gross)}</strong></p>
</div>;

const Margin = ({ e, id }) => {
  if (!e || e.margin === null) return <span className="text-xs text-muted-foreground" data-testid={`order-margin-${id}`}>—</span>;
  const cls = e.margin > 0 ? "text-emerald-500" : e.margin < 0 ? "text-red-500" : "text-muted-foreground";
  return <Tooltip><TooltipTrigger asChild><span className={`font-semibold cursor-help ${cls}`} data-testid={`order-margin-${id}`}>{e.margin > 0 ? "+" : e.margin < 0 ? "−" : ""}{euro(Math.abs(e.margin))}</span></TooltipTrigger>
    <TooltipContent className="text-xs space-y-0.5" data-testid={`order-margin-detail-${id}`}>
      <p>Incasso {e.vat_rate ? "IVA esclusa" : "(senza IVA da scorporare)"}: <strong>{euro(e.revenue)}</strong></p>
      <p>− Costo a noi (IVA esclusa){e.estimated ? " · stimato" : ""}: <strong>{euro(e.cost)}</strong></p>
      <p>− Quote rete (dealer/agenzia/master): <strong>{euro(e.shares)}</strong></p>
      <p className="border-t border-border pt-0.5">= Guadagno netto admin: <strong>{euro(e.margin)}</strong></p>
    </TooltipContent></Tooltip>;
};

export default function Orders() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [orders, setOrders] = useState([]);
  const [eco, setEco] = useState({});
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const cats = useMemo(() => [...new Set(orders.map((o) => o.category).filter(Boolean))], [orders]);
  const view = useMemo(() => {
    const s = q.trim().toLowerCase().replace(/^#/, "");
    return orders.filter((o) => (!cat || o.category === cat) && (!s || [o.ref_no, o.product_name, o.phone_number].some((v) => String(v ?? "").toLowerCase().includes(s))));
  }, [orders, q, cat]);
  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (isAdmin) { api.get("/admin/orders-economics").then((r) => setEco(r.data.items || {})).catch(() => toast.error("Costi e guadagni non disponibili")); } }, [isAdmin]);

  return (
    <TooltipProvider delayDuration={150}>
    <div className="space-y-6" data-testid="orders-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Vendite</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Ordini & Ricariche</h1>
      </div>
      <OrdersToolbar q={q} setQ={setQ} cat={cat} setCat={setCat} cats={cats} count={view.length} />
      <Card className="glow-border overflow-x-auto [&_td]:!align-middle">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="w-20">N°</TableHead><TableHead>Prodotto</TableHead><TableHead>Categoria</TableHead><TableHead>Stato</TableHead><TableHead>Numero</TableHead>
            <TableHead className="text-right">Importo</TableHead>{isAdmin && <TableHead title="Netto admin, IVA esclusa: incasso senza IVA − costo a noi − quote della rete" data-testid="orders-margin-head">Guadagno netto <span className="text-[10px] text-muted-foreground">IVA escl.</span></TableHead>}<TableHead>Data</TableHead><TableHead className="text-right">Ricevuta</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {view.map((o) => (
              <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                <TableCell className="font-mono text-sm font-semibold tabular-nums whitespace-nowrap" data-testid={`order-ref-${o.id}`}>{refNo(o)}</TableCell>
                <TableCell className="font-medium max-w-[22rem]"><EntityLink kind="order" id={o.id} data-testid={`order-detail-link-${o.id}`}>{o.product_name}</EntityLink></TableCell>
                <TableCell><Badge variant="secondary" className="font-normal whitespace-nowrap" data-testid={`order-category-${o.id}`}>{catLabel(o.category)}</Badge></TableCell>
                <TableCell><StatusDot o={o} /></TableCell>
                <TableCell className="text-sm text-muted-foreground"><PhoneDisplay value={o.phone_number} data-testid={`order-phone-${o.id}`} /></TableCell>
                <TableCell className="font-medium text-right tabular-nums whitespace-nowrap">
                  {isAdmin ? <Tip testid={`order-amount-${o.id}`} content={<CostInfo e={eco[o.id]} />}>{euro(o.amount)}</Tip> : <span data-testid={`order-amount-${o.id}`}>{euro(o.amount)}</span>}</TableCell>
                {isAdmin && <TableCell><Margin e={eco[o.id]} id={o.id} /></TableCell>}
                <TableCell className="text-sm text-muted-foreground tabular-nums whitespace-nowrap">{new Date(o.created_at).toLocaleDateString("it-IT")}</TableCell>
                <TableCell className="text-right">{RECEIPT_OK.includes(o.status) && (o.amount > 0 || o.bill_amount > 0) ? <div className="inline-flex justify-end"><PurchaseReceipt compact refId={o.id} /></div> : <span className="text-xs text-muted-foreground">—</span>}</TableCell>
              </TableRow>
            ))}
            {view.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 9 : 8} className="text-center text-muted-foreground py-8" data-testid="orders-empty">{orders.length ? "Nessun ordine con questi filtri." : "Nessun ordine registrato."}</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
    </TooltipProvider>
  );
}
